import type { AvailableToSpend, SpendingPace } from '@/types';

/**
 * Cross-widget precedence rule for the "Meta diária" footer (product-review
 * release-blocker): the daily meta is the MOST BINDING of the two constraints
 * on one screen — the spending-pace reference (W2 · Ritmo: budget / 6-month
 * average / current-pace projection) and real liquidity (W1 · Disponível:
 * safe-to-spend until the next salary). Both footers (Ritmo desktop, hero
 * mobile) derive from this one selector so the number can't swap models per
 * breakpoint, and the copy names its basis instead of phrasing a statistical
 * reference as permission ("gaste até…").
 *
 * Day counts are normalized to TODAY-INCLUSIVE (the W1 semantics): the
 * remaining allowance still has to cover the rest of today.
 */
export type DailyMeta =
  | {
      /** Positive allowance — `amount`/day keeps the binding basis intact. */
      kind: 'ok';
      amount: number;
      basis: 'budget' | 'average' | 'projection' | 'liquidity';
      daysRemaining: number;
      /** Window end ("yyyy-MM-dd") — set when the liquidity basis binds. */
      windowEnd: string | null;
    }
  | {
      /** Spending already crossed the pace reference — no daily allowance exists. */
      kind: 'over-baseline';
      overBy: number;
      basis: 'budget' | 'average' | 'projection';
      daysRemaining: number;
    }
  | {
      /** Commitments exceed the money in the window — the hardest deficit. */
      kind: 'no-liquidity';
      shortfall: number;
      windowEnd: string | null;
    };

/** What the pace reference line is built from (shared with the Ritmo subline). */
export const BASELINE_LABEL: Record<SpendingPace['baselineSource'], string> = {
  budget: 'orçamento',
  average: 'média 6m',
  projection: 'ritmo atual',
};

export function selectDailyMeta(
  pace: SpendingPace | undefined,
  available: AvailableToSpend | undefined,
): DailyMeta | null {
  if (!pace && !available) return null;

  // Liquidity deficit overrides everything: with the window already negative
  // there is no honest daily allowance, whatever the pace reference says. A
  // STRICTLY negative available is a real deficit even with zero aggregated
  // bank accounts (credit-only users: 0 in banks, open faturas > 0); only the
  // exactly-zero/no-accounts case reads as "nothing connected yet".
  if (
    available &&
    (available.available < 0 ||
      (available.available === 0 && available.accounts.length > 0))
  ) {
    return {
      kind: 'no-liquidity',
      shortfall: -available.available,
      windowEnd: available.windowEnd || null,
    };
  }

  const paceDays = pace ? Math.max(1, pace.daysRemaining + 1) : 0; // today-inclusive
  const paceRemaining = pace ? pace.baselineTotal - pace.spentToDate : 0;

  if (pace && paceRemaining <= 0) {
    return {
      kind: 'over-baseline',
      overBy: -paceRemaining,
      basis: pace.baselineSource,
      daysRemaining: paceDays,
    };
  }

  const paceDaily = pace ? paceRemaining / paceDays : null;
  const liquidityDaily =
    available && available.accounts.length > 0
      ? available.available / Math.max(1, available.daysRemaining)
      : null;

  if (paceDaily === null && liquidityDaily === null) return null;

  // Ties go to the pace basis — it is the reference the user actually set.
  if (paceDaily !== null && (liquidityDaily === null || paceDaily <= liquidityDaily)) {
    return {
      kind: 'ok',
      amount: paceDaily,
      basis: pace!.baselineSource,
      daysRemaining: paceDays,
      windowEnd: null,
    };
  }

  return {
    kind: 'ok',
    amount: liquidityDaily!,
    basis: 'liquidity',
    daysRemaining: Math.max(1, available!.daysRemaining),
    windowEnd: available!.windowEnd || null,
  };
}

export interface DailyMetaParts {
  /** Muted first line ("Meta diária · restam 18 dias"). */
  title: string;
  /** Bold line, split so the amount can render in mono: `lead`+`amount`+`trail`. */
  lead: string;
  amount: string | null;
  trail: string;
  tone: 'ok' | 'warning' | 'danger';
}

/** "2026-07-31" → "31 jul" (pt-BR 3-letter month, no trailing period). */
export function formatDayMonth(isoDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const month = PT_BR_MONTHS[Number(match[2]) - 1];
  if (!month) return null;
  return `${match[3]} ${month}`;
}

const PT_BR_MONTHS = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
] as const;

/**
 * Copy for the two-line footer. `format` is the privacy-aware currency
 * formatter (`useFormatCurrency` result) so masked mode carries through.
 */
export function dailyMetaParts(
  meta: DailyMeta,
  format: (value: number, opts?: { noCents?: boolean }) => string,
): DailyMetaParts {
  switch (meta.kind) {
    case 'ok': {
      const basisLabel =
        meta.basis === 'liquidity'
          ? `pelo saldo${metaWindowSuffix(meta.windowEnd)}`
          : meta.basis === 'average'
            ? `pela ${BASELINE_LABEL.average}`
            : `pelo ${BASELINE_LABEL[meta.basis]}`;
      return {
        title: `Meta diária · restam ${meta.daysRemaining} dias`,
        lead: '',
        amount: format(meta.amount, { noCents: true }),
        trail: ` por dia · ${basisLabel}`,
        tone: 'ok',
      };
    }
    case 'over-baseline': {
      const exhausted =
        meta.basis === 'average'
          ? `${BASELINE_LABEL.average} ultrapassada`
          : `${BASELINE_LABEL[meta.basis]} esgotado`;
      const hasAmount = meta.overBy > 0;
      return {
        title: `Meta diária · restam ${meta.daysRemaining} dias`,
        lead: hasAmount ? `${exhausted} · ` : exhausted,
        amount: hasAmount ? format(meta.overBy, { noCents: true }) : null,
        trail: hasAmount ? ' acima' : '',
        tone: 'warning',
      };
    }
    case 'no-liquidity': {
      const windowLabel = meta.windowEnd ? formatDayMonth(meta.windowEnd) : null;
      return {
        title: `Meta diária${windowLabel ? ` · até ${windowLabel}` : ''}`,
        lead: meta.shortfall > 0 ? 'sem saldo livre · falta ' : 'sem saldo livre',
        amount: meta.shortfall > 0 ? format(meta.shortfall, { noCents: true }) : null,
        trail: '',
        tone: 'danger',
      };
    }
  }
}

function metaWindowSuffix(windowEnd: string | null): string {
  const label = windowEnd ? formatDayMonth(windowEnd) : null;
  return label ? ` até ${label}` : '';
}
